import { START_OVER_ID, START_OVER_LABEL } from "@sagot/constants";
import type { StepAnalysis } from "../p4-types";
import type { Definition } from "../types";

/** Share of the runs that reached a step and went on from it (`null` when nothing reached it). */
export function continuedRatio(step: Pick<StepAnalysis, "reached" | "continued">): number | null {
  return step.reached > 0 ? step.continued / step.reached : null;
}

/** The text of a reply button, looked up in the definition shown next to the numbers; the id stands in for a button that was removed since. */
export function buttonLabel(definition: Definition, stepId: string, buttonId: string): string {
  for (const path of Object.values(definition.paths)) {
    for (const step of path.steps) {
      if (step.id !== stepId || step.type !== "reply_buttons") continue;
      const label = step.buttons.find((button) => button.id === buttonId)?.label;
      return label === undefined || label === "" ? buttonId : label;
    }
  }
  return buttonId;
}

export interface AnswerRow {
  id: string;
  label: string;
  count: number;
  /** Share of all answers to this step. */
  share: number;
}

/** The answers of a Reply buttons step, most chosen first, each with its share of the answers given (`startOver`: how the Start over button reads). */
export function answerRows(step: StepAnalysis, definition: Definition, startOver: string = START_OVER_LABEL.en): AnswerRow[] {
  const entries = Object.entries(step.answers ?? {});
  const total = entries.reduce((sum, [, count]) => sum + count, 0);
  return entries
    .map(([id, count]) => ({ id, label: id === START_OVER_ID ? startOver : buttonLabel(definition, step.step_id, id), count, share: total > 0 ? count / total : 0 }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export interface OutcomeRow {
  /** The outcome the step wrote to the run log (`answered`, `no_reply` …). */
  id: string;
  count: number;
}

/** How the runs left a step, in the order the server lists the outcomes; nothing when no run has left it yet. */
export function outcomeRows(step: Pick<StepAnalysis, "outcomes">): OutcomeRow[] {
  const rows = Object.entries(step.outcomes ?? {}).map(([id, count]) => ({ id, count }));
  return rows.some((row) => row.count > 0) ? rows : [];
}

/** `A · Reply buttons · welcome`: where the step sits and what it is (the step's own name when it has one). */
export function stepLabel(step: StepAnalysis, typeName: string): string {
  return [step.path, typeName, step.name ?? step.step_id].join(" · ");
}
